import { ChevronRight } from 'lucide-react';
import ResultCard from './ResultCard';
import ResultRow from './ResultRow';
import type { ResultSelectionControls } from './resultSelection';
import type { ResultGroup, ResultViewMode } from './types';

const GroupSection = ({
  group,
  depth,
  viewMode,
  onDownload,
  navigate,
  selection,
}: {
  group: ResultGroup;
  depth: number;
  viewMode: ResultViewMode;
  onDownload: (id: string) => void;
  navigate: (path: string) => void;
  selection: ResultSelectionControls;
}) => {
  const ResultEntry = viewMode === 'compact' ? ResultRow : ResultCard;
  const hasNestedContent = group.children.length > 0 || group.results.length > 0;

  return (
    <details
      className={`group/result-section rounded-xl border ${
        depth === 0 ? 'border-gray-200 bg-white shadow-sm' : 'border-gray-100 bg-gray-50/60'
      }`}
    >
      <summary className="flex cursor-pointer select-none items-center justify-between gap-3 px-4 py-3 list-none">
        <div className="flex min-w-0 items-center gap-2">
          <ChevronRight className="w-4 h-4 shrink-0 text-gray-400 transition-transform group-open/result-section:rotate-90" />
          <span
            className={`truncate ${depth === 0 ? 'text-sm font-semibold text-gray-900' : 'text-sm font-medium text-gray-700'}`}
            title={group.title || group.label}
          >
            {group.label}
          </span>
        </div>
        <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
          {group.count} result{group.count !== 1 ? 's' : ''}
        </span>
      </summary>

      {hasNestedContent && (
        <div className="space-y-3 border-t border-gray-100 p-3">
          {group.children.map((child) => (
            <GroupSection
              key={child.key}
              group={child}
              depth={depth + 1}
              viewMode={viewMode}
              onDownload={onDownload}
              navigate={navigate}
              selection={selection}
            />
          ))}
          {group.results.map((result) => (
            <ResultEntry
              key={result.id}
              result={result}
              onDownload={onDownload}
              navigate={navigate}
              selection={selection}
            />
          ))}
        </div>
      )}
    </details>
  );
};

const GroupedResults = ({
  groups,
  viewMode,
  onDownload,
  navigate,
  selection,
}: {
  groups: ResultGroup[];
  viewMode: ResultViewMode;
  onDownload: (id: string) => void;
  navigate: (path: string) => void;
  selection: ResultSelectionControls;
}) => (
  <div className="space-y-3 mt-2">
    {groups.map((group) => (
      <GroupSection
        key={group.key}
        group={group}
        depth={0}
        viewMode={viewMode}
        onDownload={onDownload}
        navigate={navigate}
        selection={selection}
      />
    ))}
  </div>
);

export default GroupedResults;
